import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components';
import AddPeopleModal from '../components/AddPeopleModal';
import StoryViewer from '../components/stories/StoryViewer';
import InboxColumn from '../components/messages/InboxColumn';
import ThreadPane, { EmptyPane } from '../components/messages/ThreadPane';
import SecureLockSheet from '../components/messages/SecureLockSheet';
import MessagesSettingsSheet from '../components/messages/MessagesSettingsSheet';
import BiometricOffer from '../components/messages/BiometricOffer';
import useSecureChats from '../components/messages/useSecureChats';
import { TOAST_MS as SECURE_TOAST_MS } from '../components/messages/secureModel';
import { T, RADIUS } from '../components/messages/messagesModel';
import { runPortalTransition, riseIn } from '../components/messages/messagesMotion';
import { cancelPortalNavigation, isPortalMoving } from '../components/transitions/portalNavigate';
import { ringPropsFor, reelIsLive } from '../components/stories/reelRing';
import { DISCO_KEYFRAMES } from '../components/wall/wallTokens';
import useViewportLock from '../utils/useViewportLock';
import useMediaQuery from '../utils/useMediaQuery';
import useTopBarHeight, { useChromeHeight } from '../hooks/useTopBarHeight';
import { useCall } from '../components/calls/CallProvider';
import { useUser, useAuth } from '../context';
import { useDialog } from '../context/DialogContext';
import messageService from '../services/messageService';
import storyService from '../services/storyService';
import friendService from '../services/friendService';
import { getActivePage, setActivePage, onActivePageChange } from '../services/activePage';
import { onMessageReceived, onThreadBumped, onThreadRead, joinThread, leaveThread } from '../services/messagesRealtime';
import { useOutbox } from '../hooks/useOutbox';

// Re-exported for the composer tests, which read them from the page.
export { COMPOSER_MAX_HEIGHT, enterSends, composerActionsCollapsed } from '../components/messages/Composer';

const THREAD_LIST_POLL_MS = 30000; // backstop for SignalR
const REELS_POLL_MS = 60000;

const MESSAGES_KEYFRAMES = `
  @keyframes prDot { 0%, 100% { opacity: .25; } 40% { opacity: 1; } }
  .pr-typing-dot { animation: prDot 1s linear infinite; }
  .pr-react-cell:hover { transform: translateY(-4px) scale(1.22); }
  .pr-react-plus:hover { transform: scale(1.1); }
  .pr-react-grid:hover { transform: scale(1.2); }
  .pr-composer-field::-webkit-scrollbar { width: 0; height: 0; }
  .pr-faces-strip::-webkit-scrollbar { display: none; }
  .pr-faces-arrow { opacity: 0; transition: opacity 150ms; }
  .pr-faces-frame:hover .pr-faces-arrow, .pr-faces-arrow:focus-visible { opacity: 1; }
  .pr-pin-key { transition: background-color 160ms, color 160ms; }
  @media (hover: hover) { .pr-pin-key:hover:not(:disabled) { background: rgba(255,255,255,.12) !important; } }
  .pr-pin-key:active:not(:disabled) { background: #fff !important; color: #000 !important; transition: none; }
  @media (prefers-reduced-motion: reduce) { .pr-typing-dot { animation: none; opacity: 1; } }
`;

/**
 * /messages — the inbox and the thread, redesigned Sept 2026 ("2a — PORTAL, SOFTENED").
 * Handoff: docs/plans/2026-09-messaging-redesign/README.md.
 *
 * This page owns the data (threads, the open thread, the Portals reels), the doorway
 * transition between list and thread, the new-message / add-people modal, the story viewer
 * and the toast. The columns draw themselves.
 */
export default function MessagesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile, walletBalance } = useUser();
  const { logout } = useAuth();
  const { showConfirm } = useDialog();
  const desktop = useMediaQuery('(min-width: 768px)');
  const isMobile = !desktop;
  // The top bar is drawn by the app shell ABOVE this page (AppChrome hosts it; the page's own
  // <Navbar> renders nothing), so the viewport box has to give up its height or the composer
  // sits below the fold by exactly the bar's height.
  const topBarHeight = useTopBarHeight();
  // With the app bar stepped out (a thread open on a phone) the chrome still holds a
  // minimized call's bar when there is one: that height, or nothing.
  const chromeHeight = useChromeHeight();
  const callCtx = useCall();

  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(searchParams.get('thread') || null);
  // On a phone the app bar steps out while a thread is open — a conversation gets the whole
  // screen, the way every chat app does it — and comes back with the list of threads. The
  // shell hosts whatever header the page declares, so not declaring one is how it leaves.
  const barHidden = isMobile && !!activeId;
  const [activeDetail, setActiveDetail] = useState(null);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);           // { kind: 'new' | 'add', fromRect }
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [reels, setReels] = useState([]);
  const [systemLines, setSystemLines] = useState({}); // threadId → [{ id, body, createdAt, system }]
  const [reelList, setReelList] = useState([]);
  const [doorRect, setDoorRect] = useState(null);
  // Bumped on every ring tap and used as the viewer's key: a tap always opens a fresh viewer,
  // even when the parameter already names that person.
  const [reelTap, setReelTap] = useState(0);
  const openReelUserId = searchParams.get('reel');
  const reelHistoryOwned = useRef(false);

  const stageRef = useRef(null);       // the content area below the top bar (mobile region)
  const layoutRef = useRef(null);      // the grid (mobile live layer)
  const paneRef = useRef(null);        // the thread pane (desktop region)
  const paneInnerRef = useRef(null);   // the pane's surface (desktop live layer)
  const toastRef = useRef(null);
  const busy = useRef(false);
  const toastTimer = useRef(null);
  const detailRequest = useRef(0);
  const loadedFor = useRef(null);
  // The secure-chats machine and the live list, reachable from callbacks built before them.
  const secureRef = useRef(null);
  const threadsRef = useRef(threads);
  threadsRef.current = threads;
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  useEffect(() => {
    const stage = stageRef.current, pane = paneRef.current;
    return () => {
      detailRequest.current += 1;
      loadedFor.current = null;
      clearTimeout(toastTimer.current);
      cancelPortalNavigation(stage);
      cancelPortalNavigation(pane);
    };
  }, []);

  const refreshThreads = useCallback(async () => {
    const r = await messageService.getThreads(getActivePage()?.id || null);
    if (r.success) setThreads(r.data?.items || r.data || []);
    setLoadingThreads(false);
  }, []);

  // The switcher beside the title: you and every Pro page you manage, each badged with its
  // unread. Reloaded with the threads, so a customer's message lights the page's face.
  const [inboxes, setInboxes] = useState(null);
  const [activePageId, setActivePageId] = useState(() => getActivePage()?.id || null);
  const loadInboxes = useCallback(async () => {
    const r = await messageService.getInboxes?.();
    if (r?.success && r.data) setInboxes(r.data);
  }, []);
  useEffect(() => { loadInboxes(); }, [loadInboxes]);
  useEffect(() => onActivePageChange((p) => setActivePageId(p?.id || null)), []);
  const switchInbox = useCallback((page) => {
    if ((page?.id || null) === (getActivePage()?.id || null)) return;
    setActivePage(page ? { id: page.id, slug: page.slug, name: page.name, avatarUrl: page.logoUrl || null, kind: page.kind, role: page.role } : null);
    setActivePageId(page?.id || null);
    setActiveId(null);
    setActiveDetail(null);
    setLoadingThreads(true);
    refreshThreads();
    loadInboxes();
  }, [refreshThreads, loadInboxes]);

  const loadDetail = useCallback(async (id) => {
    const request = ++detailRequest.current;
    if (!id) { setActiveDetail(null); return; }
    setLoadingDetail(true);
    const r = await messageService.getThread(id);
    if (request !== detailRequest.current) return;
    if (r.success) { setActiveDetail(r.data); setError(null); }
    else if (r.status === 423) {
      // Secure and shut (a deep link, a notification tap): ask for the lock instead
      // of showing a failure, and come back to this thread once it opens.
      setActiveDetail(null); setError(null); setActiveId(null);
      secureRef.current?.handleLocked(id);
    }
    else { setError(r.error || 'Failed to load thread'); setActiveDetail(null); }
    setLoadingDetail(false);
  }, []);

  const loadReels = useCallback(async () => {
    const r = await storyService.getReels({});
    if (r?.success) setReels(r.data || []);
  }, []);

  // Initial load + polling backstops
  useEffect(() => {
    refreshThreads();
    loadReels();
    const a = setInterval(refreshThreads, THREAD_LIST_POLL_MS);
    const b = setInterval(loadReels, REELS_POLL_MS);
    return () => { clearInterval(a); clearInterval(b); };
  }, [refreshThreads, loadReels]);

  // Realtime: upsert by id so one channel carries new sends, edits and unsends.
  useEffect(() => {
    const offMsg = onMessageReceived((msg) => {
      let landedInOpenThread = false;
      setActiveDetail((prev) => {
        if (!prev || prev.summary?.id !== msg.threadId) return prev;
        if (msg.deletedAt) return { ...prev, messages: prev.messages.filter((m) => m.id !== msg.id) };
        const idx = prev.messages.findIndex((m) => m.id === msg.id);
        if (idx >= 0) { const next = prev.messages.slice(); next[idx] = msg; return { ...prev, messages: next }; }
        landedInOpenThread = msg.senderId !== profile?.userId;
        return { ...prev, messages: [...prev.messages, msg] };
      });
      // A message that lands in the thread you are looking at has been seen: say so, which
      // is what turns the other side's line to "Seen just now". Not from a hidden tab.
      if (landedInOpenThread && document.visibilityState !== 'hidden') messageService.markRead(msg.threadId);
      refreshThreads();
    });
    const offRead = onThreadRead((info) => {
      if (!info?.threadId || info.userId === profile?.userId) return;
      setActiveDetail((prev) => {
        if (!prev || prev.summary?.id !== info.threadId) return prev;
        const reads = (prev.reads || []).filter((r) => r.userId !== info.userId);
        return { ...prev, reads: [...reads, { userId: info.userId, lastReadAt: info.at }] };
      });
    });
    const offBump = onThreadBumped(() => { refreshThreads(); loadInboxes(); });
    return () => { offMsg(); offRead(); offBump(); };
  }, [refreshThreads, loadInboxes, profile?.userId]);

  // The open thread rides the URL and the hub group. Loading is done by openThread (so the
  // doorway can open on a loaded pane); a deep link loads here.
  useEffect(() => {
    if (activeId) {
      setSearchParams((p) => { p.set('thread', activeId); return p; }, { replace: true });
      joinThread(activeId);
      if (loadedFor.current !== activeId) { loadedFor.current = activeId; loadDetail(activeId); }
    } else {
      loadedFor.current = null;
      setSearchParams((p) => { p.delete('thread'); return p; }, { replace: true });
    }
    return () => { if (activeId) leaveThread(activeId); };
  }, [activeId, loadDetail, setSearchParams]);

  // ── The doorway ──────────────────────────────────────────────────────────────────

  /**
   * Open a thread through the ring. On a phone the tapped row's ring is the door and the
   * whole content area goes through it; on desktop only the pane does, from its centre.
   * The thread is fetched first (briefly) so what appears in the hole is the conversation.
   */
  const openThread = useCallback(async (id, originEl = null, { animate = true } = {}) => {
    if (busy.current || (animate && isPortalMoving()) || !id || id === activeId) return;
    const sec = secureRef.current;
    if (sec && !sec.isOpenNow() && threadsRef.current.some((t) => t.id === id && t.isSecure)) { sec.openVault({ threadId: id }); return; }
    busy.current = true;
    const requestId = ++detailRequest.current;
    let committed = false;
    let ready;
    const request = messageService.getThread(id).then(r => {
      if (!r.success) { const e = new Error(r.error || 'Failed to load thread'); e.status = r.status; throw e; }
      ready = r;
      return r;
    });
    // After the loading cap, keep observing the SAME request. Never fetch twice or let a
    // late response replace a newer conversation / a page that has already unmounted.
    request.then(r => {
      if (!committed || requestId !== detailRequest.current) return;
      setActiveDetail(r.data); setLoadingDetail(false); setError(null);
    }, e => {
      if (!committed || requestId !== detailRequest.current) return;
      setActiveDetail(null); setLoadingDetail(false); setError(e.message);
    });
    try {
      const commit = (after, fetched) => {
        if (requestId !== detailRequest.current) return;
        fetched = fetched || ready;
        committed = true;
        flushSync(() => {
          setActiveId(id);
          loadedFor.current = id;
          if (fetched?.success) { setActiveDetail(fetched.data); setError(null); setLoadingDetail(false); }
          else { setActiveDetail(null); setLoadingDetail(true); setError(null); }
          setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, unreadCount: 0 } : t)));
        });
        after();
      };
      if (!animate) {
        commit(() => {}, await request);
      } else if (isMobile) {
        const region = stageRef.current;
        const box = region?.getBoundingClientRect();
        let origin = box ? { x: box.width / 2, y: box.height / 2 } : { x: 0, y: 0 };
        let doorway = false;
        if (originEl && box) {
          const r = originEl.getBoundingClientRect();
          origin = { x: r.left + r.width / 2 - box.left, y: r.top + r.height / 2 - box.top };
          doorway = true;
        }
        await runPortalTransition({ region, live: layoutRef.current, origin, doorway, apply: commit, load: () => request });
      } else {
        const region = paneRef.current;
        const origin = region ? { x: region.clientWidth / 2, y: region.clientHeight / 2 } : { x: 0, y: 0 };
        await runPortalTransition({ region, live: paneInnerRef.current, origin, apply: commit, load: () => request });
      }
    } catch (e) {
      if (requestId !== detailRequest.current) return;
      if (e.status === 423) { setActiveId(null); setActiveDetail(null); setError(null); secureRef.current?.handleLocked(id); }
      else setError(e.message || 'Could not open conversation');
    } finally {
      busy.current = false;
    }
  }, [activeId, isMobile]);

  const goBack = useCallback(async (event) => {
    if (busy.current || isPortalMoving()) return;
    detailRequest.current += 1;
    busy.current = true;
    try {
      const commit = (after) => { flushSync(() => { setActiveId(null); setActiveDetail(null); }); after(); };
      if (isMobile) {
        const region = stageRef.current;
        const box = region?.getBoundingClientRect();
        const button = event?.currentTarget?.getBoundingClientRect?.();
        const origin = box && button
          ? { x: (event.detail ? event.clientX : button.left + button.width / 2) - box.left, y: (event.detail ? event.clientY : button.top + button.height / 2) - box.top }
          : { x: region?.clientWidth / 2 || 0, y: region?.clientHeight / 2 || 0 };
        await runPortalTransition({ region, live: layoutRef.current, origin, back: true, apply: commit });
      } else {
        commit(() => {});
      }
    } finally {
      busy.current = false;
    }
  }, [isMobile]);

  const showToast = useCallback((text, ms = 1800) => {
    clearTimeout(toastTimer.current);
    setToast(text);
    toastTimer.current = setTimeout(() => setToast(null), ms);
  }, []);
  useLayoutEffect(() => { if (toast) riseIn(toastRef.current, { dy: 8, ms: 180, centred: true }); }, [toast]);

  // ── Secure chats ─────────────────────────────────────────────────────────────────
  // Handoff: ~/Downloads/design_handoff_secure_messages. The machine (lock status, the
  // view, the sheet, the auto-lock) is useSecureChats; this page lends it the thread
  // selection and the toast, and files the list by its `isSecure` flags.

  const selectThread = useCallback((id) => {
    if (id) { openThread(id, null, { animate: false }); return; }
    if (!activeIdRef.current) return;
    // Leaving a thread the lock just shut. goBack won't run while an open is in flight
    // (busy), so drop the thread outright — and retire that fetch, whose answer is now
    // a 423 nobody should act on.
    if (busy.current || !isMobile) {
      detailRequest.current += 1;
      loadedFor.current = null;
      setActiveId(null);
      setActiveDetail(null);
      setError(null);
      return;
    }
    goBack();
  }, [openThread, goBack, isMobile]);
  const secureToast = useCallback((text) => showToast(text, SECURE_TOAST_MS), [showToast]);
  const secure = useSecureChats({ threads, activeId, selectThread, showToast: secureToast, refreshThreads, userId: profile?.userId || null, isMobile });
  secureRef.current = secure;
  const inVault = secure.view === 'secure';
  // A secure thread that opened from a link while the window was still running: show it
  // where it lives.
  useEffect(() => {
    if (activeDetail?.summary?.isSecure && !inVault && secure.unlocked) secure.setView('secure');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDetail?.summary?.isSecure]);

  // ?with=<peerUserId>[&event=<eventId>] — get-or-create a thread with that peer and open it.
  useEffect(() => {
    const peerId = searchParams.get('with');
    if (!peerId) return undefined;
    const eventId = searchParams.get('event') || null;
    let cancelled = false;
    (async () => {
      const r = await messageService.createOrGetThread(peerId, eventId);
      if (cancelled) return;
      setSearchParams((p) => { p.delete('with'); p.delete('event'); return p; }, { replace: true });
      // The route entry is already a portal journey; don't add a second pane journey.
      if (r.success && r.data?.id) openThread(r.data.id, null, { animate: false });
      else setError(r.error || 'Could not open conversation');
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Portals ──────────────────────────────────────────────────────────────────────

  const liveReels = useMemo(() => reels.filter(reelIsLive), [reels]);
  const reelByUser = useMemo(() => new Map(liveReels.map((r) => [r.authorUserId, r])), [liveReels]);
  const ringFor = useCallback((userId) => ringPropsFor(reelByUser.get(userId)), [reelByUser]);
  // Rail order: own → live → unseen → seen.
  const rail = useMemo(() => {
    const rank = (r) => (r.isOwn ? 0 : ringPropsFor(r)?.state === 'live' || ringPropsFor(r)?.state === 'text' ? 1 : (r.hasUnseen || r.HasUnseen) ? 2 : 3);
    return [...liveReels].sort((a, b) => rank(a) - rank(b));
  }, [liveReels]);
  const railLive = useMemo(() => liveReels.filter((r) => r.portal).length, [liveReels]);

  const openReel = useCallback((userId, ringRect) => {
    setReelList(rail);
    setReelTap((n) => n + 1);
    setDoorRect(ringRect ? { left: ringRect.left, top: ringRect.top, width: ringRect.width, height: ringRect.height } : null);
    reelHistoryOwned.current = !openReelUserId;
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('reel', userId); return next; }, { replace: !!openReelUserId });
  }, [rail, openReelUserId, setSearchParams]);
  const closeReel = useCallback(() => {
    // Only pop an entry this page actually pushed. Reloaded/deep-linked viewers have no
    // owned history entry; blindly popping can reopen the same reel or leave Messages.
    if (reelHistoryOwned.current) navigate(-1);
    else setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('reel'); return next; }, { replace: true });
    reelHistoryOwned.current = false;
    loadReels();
  }, [navigate, setSearchParams, loadReels]);

  // ── Threads ──────────────────────────────────────────────────────────────────────

  const unreadTotal = useMemo(() => threads.reduce((n, t) => n + (t.isSecure ? 0 : t.unreadCount || 0), 0), [threads]);
  const filteredThreads = useMemo(() => threads.filter((t) => {
    // The secure view is its own list: only secure threads, search only, no pills.
    if (inVault) {
      if (!t.isSecure) return false;
      if (search) {
        const q = search.toLowerCase();
        return `${t.peer?.displayName || ''} ${t.peer?.handle || ''} ${t.eventTitle || ''}`.toLowerCase().includes(q);
      }
      return true;
    }
    if (t.isSecure) return false;
    if (filter === 'friends' && t.kind !== 'friend') return false;
    if (filter === 'hosts' && t.kind !== 'host') return false;
    if (filter === 'unread' && !(t.unreadCount > 0)) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${t.peer?.displayName || ''} ${t.peer?.handle || ''} ${t.eventTitle || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [threads, filter, search, inVault]);

  const handleDeleteThread = async (id) => {
    const ok = await showConfirm({
      title: 'Delete conversation?',
      message: 'This removes it from your inbox only. The other people keep the conversation.',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    const r = await messageService.deleteThread(id);
    if (r.success) { if (id === activeId) goBack(); refreshThreads(); }
  };

  const handleMarkUnread = async (id) => {
    const r = await messageService.markUnread(id);
    if (r.success) { if (id === activeId) goBack(); refreshThreads(); }
  };

  const handleViewProfile = (thread) => {
    const href = thread?.peer?.href || (thread?.peer?.userId ? `/users/${thread.peer.userId}` : null);
    if (href) navigate(href);
  };


  // ── Modal: new message / add people ──────────────────────────────────────────────

  const openNew = (fromRect) => setModal({ kind: 'new', fromRect: fromRect || null });
  const openAdd = (fromRect) => setModal({ kind: 'add', fromRect: fromRect || null });
  const closeModal = () => setModal(null);

  const startConversation = async (userIds) => {
    const r = await messageService.startConversation(userIds, getActivePage()?.id || null);
    setModal(null);
    if (r.success && r.data?.id) {
      const id = r.data.id;
      // A new thread lands at the top; an existing one is simply opened.
      setThreads((prev) => (prev.some((t) => t.id === id) ? prev : [r.data, ...prev]));
      refreshThreads();
      openThread(id);
    } else setError(r.error || 'Could not start conversation');
  };

  const addPeople = async (userIds, picked = []) => {
    if (!activeDetail?.summary?.id) return;
    const id = activeDetail.summary.id;
    const r = await messageService.addParticipants(id, userIds);
    setModal(null);
    if (!r.success) { setError(r.error || 'Could not add them'); return; }
    const names = picked.map((p) => (p.displayName || '').toUpperCase()).filter(Boolean).join(', ');
    if (names) {
      setSystemLines((prev) => ({
        ...prev,
        [id]: [...(prev[id] || []), { id: `sys:${Date.now()}`, system: true, body: `YOU ADDED ${names}`, createdAt: new Date().toISOString(), senderId: profile?.userId }],
      }));
    }
    loadDetail(id);
    refreshThreads();
  };

  const onLocalSent = useCallback((msg) => {
    setActiveDetail((prev) => {
      if (!prev) return prev;
      const idx = prev.messages.findIndex((m) => m.id === msg.id);
      if (idx >= 0) { const next = prev.messages.slice(); next[idx] = msg; return { ...prev, messages: next }; }
      return { ...prev, messages: [...prev.messages, msg] };
    });
    refreshThreads();
  }, [refreshThreads]);

  const outbox = useOutbox({ viewerUserId: profile?.userId, onDelivered: onLocalSent });

  // Suggestions for the modal: people with a Portal up right now, you excluded.
  // A new personal chat (or a new member) has to be a friend (Nov 2026), so the picker searches
  // friends. Speaking as a Pro page keeps the open search: anyone can talk to a page.
  const [friends, setFriends] = useState(null);
  useEffect(() => {
    if (!modal || friends) return;
    friendService.getFriends().then((r) => setFriends(Array.isArray(r?.data) ? r.data : []));
  }, [modal, friends]);
  const personalPick = !(modal?.kind === 'new' && getActivePage()?.id);
  const searchFriends = useCallback(async (q, limit = 20) => {
    const list = friends || (await friendService.getFriends())?.data || [];
    const needle = (q || '').trim().toLowerCase().replace(/^@/, '');
    const data = list
      .filter((f) => !needle || (f.displayName || '').toLowerCase().includes(needle) || (f.handle || '').toLowerCase().includes(needle))
      .slice(0, limit);
    return { success: true, data };
  }, [friends]);
  const friendIds = useMemo(() => new Set((friends || []).map((f) => f.userId)), [friends]);
  const suggestions = useMemo(() => liveReels
    .filter((r) => !r.isOwn && r.portal && (!personalPick || friendIds.has(r.authorUserId)))
    .map((r) => ({ userId: r.authorUserId, displayName: r.displayName, handle: r.handle, avatarUrl: r.avatarUrl })), [liveReels, personalPick, friendIds]);

  // Messages owns the whole screen: no document scrolling while it's mounted.
  useViewportLock();

  const isNew = modal?.kind === 'new';

  return (
    // The inbox runs white to the bottom edge under the floating tab bar (InboxColumn keeps
    // its last row clear of the bar); an open thread owns the whole screen.
    <div data-testid="messages-viewport" data-bar={barHidden ? 'hidden' : 'shown'} className="app-viewport flex flex-col overflow-hidden" style={{ background: isMobile ? T.white : T.canvas, height: `calc(var(--app-vh, 100dvh) - ${barHidden ? chromeHeight : topBarHeight}px)`, boxSizing: 'border-box' }}>
      <style>{DISCO_KEYFRAMES}{MESSAGES_KEYFRAMES}</style>
      {!barHidden && <Navbar user={profile} walletBalance={walletBalance} onLogout={logout} onLogoClick={() => navigate('/')} />}
      <div ref={stageRef} className="relative flex-1 min-h-0 w-full overflow-hidden">
        {/* One explicit row of minmax(0, 1fr): an auto row would size itself to the inbox
            list's content and push the composer below the viewport. */}
        <div
          ref={layoutRef}
          data-live
          className="relative h-full max-w-[1280px] mx-auto grid grid-cols-1 grid-rows-[minmax(0,1fr)] md:grid-cols-[360px_1fr]"
          style={{ background: T.canvas }}
        >
          <div className={`${activeId ? 'hidden md:flex' : 'flex'} h-full min-h-0 flex-col overflow-hidden`}>
            <InboxColumn
              threads={filteredThreads}
              activeId={activeId}
              onSelect={openThread}
              onDelete={handleDeleteThread}
              onMarkUnread={handleMarkUnread}
              onViewProfile={handleViewProfile}
              filter={filter}
              setFilter={setFilter}
              search={search}
              setSearch={setSearch}
              loading={loadingThreads}
              onNewMessage={openNew}
              unreadTotal={unreadTotal}
              inboxes={inboxes}
              activePageId={activePageId}
              viewer={profile}
              onSwitchInbox={switchInbox}
              rail={rail}
              railLive={railLive}
              ringFor={ringFor}
              onOpenReel={openReel}
              onSendOne={() => navigate('/portals/new')}
              isMobile={isMobile}
              secure={secure}
              onMoveToSecure={secure.moveToSecure}
              onRemoveFromSecure={secure.removeFromSecure}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          </div>
          <div ref={paneRef} className={`${activeId ? 'flex' : 'hidden md:flex'} relative h-full min-h-0 overflow-hidden`}>
            <div ref={paneInnerRef} className="absolute inset-0 flex flex-col" style={{ background: T.canvas }}>
              {activeId ? (
                <ThreadPane
                  detail={activeDetail}
                  loading={loadingDetail}
                  error={error}
                  onBack={goBack}
                  outbox={outbox}
                  onPeerProfile={(href) => (href ? navigate(href) : null)}
                  onRefresh={() => { loadDetail(activeId); refreshThreads(); }}
                  viewerUserId={profile?.userId}
                  ringFor={ringFor}
                  onOpenReel={openReel}
                  onAddPeople={openAdd}
                  systemLines={systemLines[activeId] || []}
                  onToast={showToast}
                  isMobile={isMobile}
                  motionLayerRef={stageRef}
                  locked={!!activeDetail?.summary?.isSecure && !secure.unlocked}
                  onUnlock={() => secure.openVault({ threadId: activeId })}
                  reelFor={(userId) => reelByUser.get(userId) || null}
                  onToggleSecure={(id, next) => (next ? secure.moveToSecure(id) : secure.removeFromSecure(id))}
                  onDeleteConversation={handleDeleteThread}
                  onBlocked={() => { goBack(); refreshThreads(); }}
                  onStartCall={callCtx ? (video, e) => {
                    const s = activeDetail?.summary;
                    const peers = s?.isGroup
                      ? (activeDetail?.members || []).filter((m) => m.userId !== profile?.userId).map((m) => ({ userId: m.userId, name: m.displayName, avatarUrl: m.avatarUrl || null }))
                      : s?.peer?.userId ? [{ userId: s.peer.userId, name: s.peer.displayName, avatarUrl: s.peer.avatarUrl || null }] : [];
                    callCtx.start({ threadId: s?.id, video, peers, originEl: e?.currentTarget || null });
                  } : null}
                  callActive={callCtx?.call && callCtx.call.threadId === activeId ? (callCtx.call.video ? 'video' : 'voice') : null}
                />
              ) : (
                <EmptyPane onNewMessage={openNew} />
              )}
            </div>
          </div>
        </div>

        {!secure.modal && <BiometricOffer offer={secure.offer} />}
        {settingsOpen && !secure.modal && (
          <MessagesSettingsSheet secure={secure} onClose={() => setSettingsOpen(false)} />
        )}
        {secure.modal && (
          <SecureLockSheet key={secure.modal.key} modal={secure.modal} {...secure.sheet} />
        )}

        {toast && (
          <div
            ref={toastRef}
            data-toast
            role="status"
            className="font-mono font-bold pointer-events-none"
            style={{ position: 'absolute', left: '50%', bottom: 104, transform: 'translateX(-50%)', zIndex: 90, background: T.ink, color: T.white, borderRadius: RADIUS.pill, height: 34, padding: '0 16px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', fontSize: 10, letterSpacing: '.12em' }}
          >
            {toast}
          </div>
        )}
      </div>

      <AddPeopleModal
        title={isNew ? 'New message' : 'Add people'}
        ctaLabel={isNew ? 'Start chat' : 'Add'}
        showCount={!isNew}
        placeholder={personalPick ? 'Search your friends…' : 'Search people to message…'}
        searchFn={personalPick ? searchFriends : undefined}
        isOpen={!!modal}
        fromRect={modal?.fromRect || null}
        onClose={closeModal}
        onAdd={isNew ? startConversation : addPeople}
        excludeUserIds={[profile?.userId, ...(isNew ? [] : [activeDetail?.summary?.peer?.userId])].filter(Boolean)}
        suggestions={suggestions}
        suggestionsLabel="PORTALS LIVE NOW"
        ringFor={ringFor}
      />

      {openReelUserId && (reelList.length > 0 || rail.length > 0) && (
        <StoryViewer key={`${openReelUserId}:${reelTap}`} reels={reelList.length ? reelList : rail} startUserId={openReelUserId} doorRect={doorRect} onClose={closeReel} />
      )}
    </div>
  );
}
